import { useState } from 'react';
import { CircleAlert, CircleCheck, CircleX, Pencil, Trash2 } from 'lucide-react';
import type { ProjectStatus, ProjectUpdateView, ProjectView } from '@vwt/contracts';
import { Avatar, Badge, Button, NativeSelect, Textarea, toast } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { MarkdownEditor, MarkdownView } from '../../components/markdown.js';
import { QueryState, SectionTitle, TimeAgo } from '../../components/common.js';

const STATUSES: ProjectStatus[] = ['ON_TRACK', 'AT_RISK', 'OFF_TRACK'];
const tone = { ON_TRACK: 'success', AT_RISK: 'warning', OFF_TRACK: 'danger' } as const;
const icon = { ON_TRACK: CircleCheck, AT_RISK: CircleAlert, OFF_TRACK: CircleX } as const;

/** Status badge with icon and text (never color alone). */
export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const { t } = useTranslation();
  const Icon = icon[status];
  return (
    <Badge tone={tone[status]}>
      <Icon /> {t(`projects.updates.status.${status}`)}
    </Badge>
  );
}

/** Status posts of a project: the newest first, a form for the people who steer it, and replies. */
export function ProjectUpdates({ project }: { project: ProjectView }) {
  const { t } = useTranslation();
  const updates = useApiQuery('listProjectUpdates', { params: { id: project.id } });
  const post = useApiMutation('postProjectUpdate', { invalidate: ['listProjectUpdates', 'getProject', 'listProjects'] });
  const [status, setStatus] = useState<ProjectStatus>(project.latestUpdate?.status ?? 'ON_TRACK');
  const [body, setBody] = useState('');
  const [key, setKey] = useState(newIdempotencyKey);
  const canPost = project.availableActions.includes('postUpdate');
  const submit = async () => {
    try {
      await post.mutateAsync({ params: { id: project.id }, body: { status, bodyMarkdown: body }, idempotencyKey: key });
      setBody('');
      setKey(newIdempotencyKey());
      toast.success(t('projects.updates.posted'));
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>{t('projects.updates.title')}</SectionTitle>
      {canPost ? (
        <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-border p-3">
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)} aria-label={t('projects.updates.statusLabel')} className="w-auto self-start">
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`projects.updates.status.${s}`)}
              </option>
            ))}
          </NativeSelect>
          <MarkdownEditor value={body} onChange={setBody} minRows={3} placeholder={t('projects.updates.placeholder')} onSubmit={() => body.trim() && void submit()} ariaLabel={t('projects.updates.title')} />
          <Button size="sm" variant="primary" className="self-end" disabled={!body.trim()} loading={post.isPending} onClick={() => void submit()}>
            {t('projects.updates.post')}
          </Button>
        </div>
      ) : null}
      <QueryState query={updates} empty={updates.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('projects.updates.empty')}</p> : undefined}>
        <ol className="flex flex-col gap-3">
          {(updates.data?.data ?? []).map((u) => (
            <UpdateItem key={u.id} projectId={project.id} update={u} />
          ))}
        </ol>
      </QueryState>
    </section>
  );
}

function UpdateItem({ projectId, update }: { projectId: string; update: ProjectUpdateView }) {
  const { t } = useTranslation();
  const edit = useApiMutation('editProjectUpdate', { invalidate: ['listProjectUpdates', 'getProject', 'listProjects'] });
  const remove = useApiMutation('deleteProjectUpdate', { invalidate: ['listProjectUpdates', 'getProject', 'listProjects'] });
  const reply = useApiMutation('replyToProjectUpdate', { invalidate: ['listProjectUpdates'] });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(update.bodyMarkdown);
  const [answer, setAnswer] = useState('');
  return (
    <li className="rounded-[var(--radius)] border border-border bg-surface px-3 py-2">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
        <ProjectStatusBadge status={update.status} />
        <span className="font-medium text-fg">{update.author?.displayName ?? t('common.unknown')}</span>
        <TimeAgo iso={update.createdAt} />
        {update.editedAt ? <span>· {t('projects.updates.edited')}</span> : null}
        {update.canEdit ? (
          <span className="ml-auto flex gap-1">
            <Button variant="ghost" size="iconSm" aria-label={t('common.edit')} onClick={() => (setDraft(update.bodyMarkdown), setEditing(true))}>
              <Pencil />
            </Button>
            <Button variant="ghost" size="iconSm" aria-label={t('common.delete')} onClick={() => void remove.mutateAsync({ params: { id: projectId, childId: update.id } }).catch(toastError)}>
              <Trash2 />
            </Button>
          </span>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <MarkdownEditor value={draft} onChange={setDraft} minRows={3} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!draft.trim()}
              onClick={() =>
                void edit
                  .mutateAsync({ params: { id: projectId, childId: update.id }, body: { bodyMarkdown: draft } })
                  .then(() => setEditing(false))
                  .catch(toastError)
              }
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : (
        <MarkdownView source={update.bodyMarkdown} />
      )}
      {update.replies.length ? (
        <ul className="mt-2 flex flex-col gap-2 border-l-2 border-border pl-3">
          {update.replies.map((r) => (
            <li key={r.id} className="flex gap-2 text-sm">
              <Avatar name={r.author?.displayName ?? '?'} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-fg-subtle">
                  <span className="font-medium text-fg">{r.author?.displayName ?? t('common.unknown')}</span> · <TimeAgo iso={r.createdAt} />
                </p>
                <MarkdownView source={r.bodyMarkdown} />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      <form
        className="mt-2 flex gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!answer.trim()) return;
          try {
            await reply.mutateAsync({ params: { id: projectId, childId: update.id }, body: { bodyMarkdown: answer }, idempotencyKey: newIdempotencyKey() });
            setAnswer('');
          } catch (error) {
            toastError(error);
          }
        }}
      >
        <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={1} placeholder={t('projects.updates.replyPlaceholder')} aria-label={t('projects.updates.reply')} className="min-h-8" />
        <Button type="submit" size="sm" variant="secondary" disabled={!answer.trim()} loading={reply.isPending}>
          {t('projects.updates.reply')}
        </Button>
      </form>
    </li>
  );
}
